import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { BUSINESS, ASSETS, SERVICES, AREAS } from "@/data/site";
import { QuoteButton, CallButton } from "@/components/Buttons";
import { Reveal, SectionLabel } from "@/components/Reveal";

export const Footer = () => (
  <footer className="relative overflow-hidden bg-brand-blue text-slate-300 grain" data-testid="site-footer">
    {/* CTA band */}
    <div className="border-b border-white/10">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-16 md:py-24">
        <Reveal>
          <SectionLabel dark>Let's Get Started</SectionLabel>
          <h2 className="mt-5 max-w-3xl font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white text-balance">
            Ready to upgrade your comfort?
          </h2>
          <p className="mt-5 max-w-xl text-lg text-slate-400">
            Talk to our team about air conditioning, electrical or heat pump hot water. Free, no-obligation quotes.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <QuoteButton testId="footer-quote" />
            <CallButton dark testId="footer-call" />
          </div>
        </Reveal>
      </div>
    </div>

    {/* Links */}
    <div className="mx-auto max-w-7xl px-5 lg:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <img src={ASSETS.logo} alt="Access Electrical and Air Conditioning logo" className="h-[80px] w-auto object-contain bg-white/95 rounded-lg p-2" />
        <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-xs">
          Electrical, heating and cooling specialists serving Victoria and New South Wales — backed by {BUSINESS.yearsInBusiness} years of service and {BUSINESS.combinedExperience} years combined industry experience.
        </p>
      </div>

      <div>
        <h4 className="font-display text-sm uppercase tracking-widest text-white">Services</h4>
        <ul className="mt-5 space-y-3 text-sm">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link to={`/services/${s.slug}`} className="text-slate-400 hover:text-brand-gold transition-colors">
                {s.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-display text-sm uppercase tracking-widest text-white">Areas We Serve</h4>
        <ul className="mt-5 space-y-3 text-sm text-slate-400">
          {[...AREAS.vic.slice(0, 5), ...AREAS.nsw].map((a) => (
            <li key={a}>{a}</li>
          ))}
          <li>
            <Link to="/areas-we-serve" className="inline-flex items-center gap-1 text-brand-gold hover:underline">
              View all areas <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-display text-sm uppercase tracking-widest text-white">Contact</h4>
        <ul className="mt-5 space-y-4 text-sm">
          <li>
            <a href={BUSINESS.phoneHref} className="flex items-start gap-3 text-slate-400 hover:text-brand-gold transition-colors">
              <Phone className="mt-0.5 h-4 w-4 shrink-0" /> {BUSINESS.phone}
            </a>
          </li>
          <li>
            <a href={BUSINESS.emailHref} className="flex items-start gap-3 break-all text-slate-400 hover:text-brand-gold transition-colors">
              <Mail className="mt-0.5 h-4 w-4 shrink-0" /> {BUSINESS.email}
            </a>
          </li>
          {BUSINESS.offices.map((o) => (
            <li key={o.address} className="flex items-start gap-3 text-slate-400">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {o.address}
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <p>{BUSINESS.name} · ABN {BUSINESS.abn}</p>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" className="hover:text-slate-300">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-slate-300">Terms &amp; Conditions</Link>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </div>
    </div>
  </footer>
);
